import { createLeaderElection, BroadcastChannel } from 'broadcast-channel';
import type { LeaderElector } from 'broadcast-channel';
import { useEffect, useState, useCallback, useRef } from 'react';

// Generic broadcast channel hook for general purposes
export function useBroadcastChannel<T = unknown>(
  channelName: string,
  onMessage?: (data: T) => void,
) {
  const [isLeader, setIsLeader] = useState(false);
  const [messages, setMessages] = useState<T[]>([]);
  // A ref, not state: as state the channel landed one render after mount, republishing
  // `sendMessage` and re-running every caller effect that depends on it.
  const channelRef = useRef<BroadcastChannel | null>(null);
  const leaderElectionRef = useRef<LeaderElector | null>(null);
  const mountedRef = useRef(true);

  // Ensure the component is still mounted before updating state
  // Cleanup on unmount
  useEffect(() => {
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    const leaderElection = createLeaderElection(channel);
    leaderElectionRef.current = leaderElection;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget leader election; the callback only sets local state
    leaderElection.awaitLeadership().then(() => {
      if (mountedRef.current) {
        setIsLeader(true);
      }
    });

    channel.onmessage = (message: T) => {
      if (mountedRef.current) {
        setMessages((prev) => [...prev, message]);
        if (onMessage) {
          onMessage(message);
        }
      }
    };

    return () => {
      mountedRef.current = false;
      if (leaderElectionRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget leader resignation on unmount; nothing observes the result
        leaderElectionRef.current.die();
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget channel close on unmount; nothing observes the result
      channel.close();
    };
  }, [channelName, onMessage]);

  // Function to send messages through the broadcast channel
  const sendMessage = useCallback((message: T) => {
    if (channelRef.current && mountedRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget broadcast; a rejection is dropped, as it is today
      channelRef.current.postMessage(message);
    }
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    isLeader,
    messages,
    sendMessage,
    broadcast: sendMessage,
  };
}
